import { Capacitor } from '@capacitor/core';
import { PushNotifications, type ActionPerformed, type PushNotificationSchema, type RegistrationError, type Token } from '@capacitor/push-notifications';

// Push notification adapter (Owner contract 2026-09-13 §9 / §10 / §40 / §51). One contract, two
// implementations: the Capacitor plugin on a native app (imported STATICALLY like the camera — a
// dynamic import that never resolves on device is a documented old-line failure mode) and an
// "unavailable" adapter everywhere else (web preview, jsdom). Tests inject a fake through
// setPushAdapterForTests. Nothing here decides anything about AI jobs: it only hands tokens and
// taps to src/state/pushRegistration.ts.
//
// Windows has no Xcode: this file is type-checked and bundled here, and its behaviour on a device
// is DEVICE TEST REQUIRED; it also needs the Push Notifications capability on the App ID, the
// aps-environment entitlement and APNs secrets on the server (CODE COMPLETE — EXTERNAL CONFIG
// REQUIRED until those exist).
export type PushPermission = 'granted' | 'denied' | 'prompt' | 'unavailable';

export type PushNotificationData = Record<string, unknown>;

export interface PushAdapter {
  available(): boolean;
  checkPermission(): Promise<PushPermission>;
  requestPermission(): Promise<PushPermission>;
  register(): Promise<void>;
  onRegistration(listener: (token: string) => void): () => void;
  onRegistrationError(listener: (message: string) => void): () => void;
  // The app was in the foreground when the notification arrived.
  onNotificationReceived(listener: (data: PushNotificationData) => void): () => void;
  // The inspector tapped the notification (from the background, or after a cold start).
  onNotificationTapped(listener: (data: PushNotificationData) => void): () => void;
}

function mapPermission(state: string): PushPermission {
  if (state === 'granted') return 'granted';
  if (state === 'denied') return 'denied';
  return 'prompt';
}

// A listener registration whose removal is safe even if the plugin's promise has not settled yet.
function listen<T>(add: (cb: (event: T) => void) => Promise<{ remove: () => Promise<void> }>, cb: (event: T) => void): () => void {
  let removed = false;
  const handle = add((event) => {
    if (!removed) cb(event);
  });
  return () => {
    removed = true;
    void handle.then((h) => h.remove()).catch(() => undefined);
  };
}

// iOS only. The Android app has no Firebase project (no google-services.json) and the server sends APNs only, so
// registering there does nothing useful — and it CRASHES: the plugin's register() calls
// FirebaseMessaging.getInstance() and the process dies with "Default FirebaseApp is not initialized" (reproduced on
// the emulator 2026-10-18 after granting notifications; Android 12 and older grant them by default, so every launch
// of a signed-in app died). The privacy page says the Android app has no push.
export function pushSupportedOn(platform: string): boolean {
  return platform === 'ios';
}

export const capacitorPushAdapter: PushAdapter = {
  available: () => Capacitor.isNativePlatform() && pushSupportedOn(Capacitor.getPlatform()),
  async checkPermission() {
    try {
      return mapPermission((await PushNotifications.checkPermissions()).receive);
    } catch {
      return 'unavailable';
    }
  },
  async requestPermission() {
    try {
      return mapPermission((await PushNotifications.requestPermissions()).receive);
    } catch {
      return 'unavailable';
    }
  },
  async register() {
    await PushNotifications.register();
  },
  onRegistration: (listener) => listen<Token>((cb) => PushNotifications.addListener('registration', cb), (token) => listener(token.value)),
  onRegistrationError: (listener) => listen<RegistrationError>((cb) => PushNotifications.addListener('registrationError', cb), (error) => listener(typeof error.error === 'string' ? error.error : 'registration failed')),
  onNotificationReceived: (listener) => listen<PushNotificationSchema>((cb) => PushNotifications.addListener('pushNotificationReceived', cb), (notification) => listener((notification.data ?? {}) as PushNotificationData)),
  onNotificationTapped: (listener) => listen<ActionPerformed>((cb) => PushNotifications.addListener('pushNotificationActionPerformed', cb), (action) => listener((action.notification.data ?? {}) as PushNotificationData))
};

export const unavailablePushAdapter: PushAdapter = {
  available: () => false,
  checkPermission: async () => 'unavailable',
  requestPermission: async () => 'unavailable',
  register: async () => undefined,
  onRegistration: () => () => undefined,
  onRegistrationError: () => () => undefined,
  onNotificationReceived: () => () => undefined,
  onNotificationTapped: () => () => undefined
};

let override: PushAdapter | null = null;

export function getPushAdapter(): PushAdapter {
  if (override) return override;
  return Capacitor.isNativePlatform() ? capacitorPushAdapter : unavailablePushAdapter;
}

export function setPushAdapterForTests(adapter: PushAdapter | null): void {
  override = adapter;
}
